import { Alert, Button, Group, List, Modal, Stack, Text } from '@mantine/core';
import { ClientListItem } from 'src/admin/users/types';
import { formatName, NameFormat } from 'src/utils/nameFormatting';
import { useAdvisors } from '../hooks/useAdvisors';

type Props = {
  opened: boolean;
  conflictingMembers: ClientListItem[];
  targetAdvisorUserId?: string;
  // Completes "... will reassign them to <adviser>", e.g. "Creating this family".
  actionDescription: string;
  confirmLabel: string;
  isLoading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

export function ReassignAdvisorConfirmModal({
  opened,
  conflictingMembers,
  targetAdvisorUserId,
  actionDescription,
  confirmLabel,
  isLoading,
  onConfirm,
  onCancel,
}: Props) {
  const { advisors } = useAdvisors();

  const advisorName = (advisorUserId?: string) => {
    const advisor = advisors.find(candidate => candidate.id === advisorUserId);
    return advisor
      ? formatName(advisor.firstName, advisor.lastName, NameFormat.NATURAL)
      : 'another adviser';
  };

  const isSingleClient = conflictingMembers.length === 1;

  return (
    <Modal
      opened={opened}
      onClose={onCancel}
      title={
        isSingleClient
          ? "Reassign this client to the family's adviser?"
          : "Reassign clients to this family's adviser?"
      }
      centered
    >
      <Stack gap="md">
        <Text>
          {isSingleClient
            ? 'This client is currently advised by someone else.'
            : 'These clients are currently advised by someone else.'}{' '}
          {actionDescription} will reassign them to {advisorName(targetAdvisorUserId)}.
        </Text>
        <List>
          {conflictingMembers.map(member => (
            <List.Item key={member.id}>
              {formatName(member.firstName, member.lastName, NameFormat.NATURAL)} (currently advised
              by {advisorName(member.advisorUserId)})
            </List.Item>
          ))}
        </List>
        <Alert color="yellow">Their previous adviser will no longer be able to see them.</Alert>
        <Group justify="flex-end">
          <Button variant="outline-black" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="black" onClick={onConfirm} loading={isLoading}>
            {confirmLabel}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
